import { useEffect, useState } from "react";
import { X, FileText, Loader2 } from "lucide-react";

// =====================================================================
// ComparePlansDialog — pick a second plan to put side-by-side with the
// current one, then download the comparison PDF.
//
// `plans` is the patient's full list (from list_treatment_plans). We
// filter out the current plan and let the user pick one of the others
// as Option B.
// =====================================================================
function formatPHP(centavos) {
  return "₱" + Math.round((centavos ?? 0) / 100).toLocaleString("en-PH");
}
function formatDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return String(d); }
}

export default function ComparePlansDialog({
  open,
  onOpenChange,
  currentPlanId,
  plans = [],          // [{id, status, total_centavos, item_count, notes, created_at, ...}]
  onCompare,           // (otherPlanId) => Promise<void>
}) {
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setPicked(null); setSubmitting(false); }
  }, [open]);

  const others = (plans || []).filter((p) => p.id !== currentPlanId);

  const close = () => { if (!submitting) onOpenChange(false); };
  async function go() {
    if (!picked || submitting) return;
    setSubmitting(true);
    try { await onCompare?.(picked); }
    finally { setSubmitting(false); onOpenChange(false); }
  }

  if (!open) return null;
  return (
    <div
      role="dialog" aria-modal="true"
      data-testid="compare-plans-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Compare two plans</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              The current plan is Option A. Pick which plan to put side-by-side as Option B.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            data-testid="compare-plans-close"
            className="p-1 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {others.length === 0 ? (
            <div className="text-center py-8" data-testid="compare-plans-empty">
              <div className="w-12 h-12 rounded-full bg-slate-100 mx-auto flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-400" />
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-900">No other plans yet</p>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                Create a second plan for this patient (e.g. with a different material) and come back
                to generate a side-by-side quotation.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 -mx-2" data-testid="compare-plans-list">
              {others.map((p) => {
                const isPicked = picked === p.id;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setPicked(p.id)}
                      data-testid={`compare-plans-pick-${p.id}`}
                      aria-pressed={isPicked}
                      className={`w-full text-left px-3 py-2.5 mx-1 rounded-lg flex items-center gap-3 transition-colors ${
                        isPicked ? "bg-teal-50 ring-1 ring-teal-300" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full border ${isPicked ? "border-teal-600 bg-teal-600" : "border-slate-300"}`}>
                        {isPicked ? <span className="block w-1.5 h-1.5 rounded-full bg-white mx-auto mt-1" /> : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {p.notes ? p.notes : `Plan from ${formatDate(p.created_at)}`}
                          <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{p.status}</span>
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          {p.item_count ?? 0} item{(p.item_count ?? 0) === 1 ? "" : "s"}
                          {" · "}{formatPHP(p.total_centavos)}
                          {" · "}{formatDate(p.created_at)}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            data-testid="compare-plans-cancel"
            className="px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={go}
            disabled={!picked || submitting || others.length === 0}
            data-testid="compare-plans-go"
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Download comparison PDF
          </button>
        </div>
      </div>
    </div>
  );
}
